import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useField } from 'react-final-form';
import { isAfter, isBefore, max, parse, startOfDay } from 'date-fns';

import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { getKnoSupervisionStartDate } from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import {
  START_DATE_BLOCK_ID,
  START_TIME_BLOCK_ID,
  STOP_DATE_BLOCK_ID,
  STOP_TIME_BLOCK_ID,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataCreated,
  getKnmExcludeDates,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusReadyForApprove,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { TimePickerField } from 'UIPackage/MainModule/components/TimePickerField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { knmInfoGetExcludeDates } from 'KnmPackage/MainModule/actions/knmActions';
import { IKnmErknmFormReason } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormReason';

import { IErknmInfoExcludeDatesResponse } from '../interfaces/IErknmInfoExcludeDatesResponse';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { formatDate, parseDate } from '../../../CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { getMaxDate } from '../../MainModule/common/getMaxDate';
import { isDateValid } from '../../MainModule/common/isDateValid';
import { subscribeGetExcludeDates } from '../../MainModule/common/subscribeGetExcludeDates';
import styles from '../../MainModule/components/CreateErknmModal.module.scss';
import { getUserRoleIsSupervisor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { ErknmApproveRemarkAddButton } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemarkAddButton';
import { ErknmApproveRemarkSectionCodeEnum } from '../../ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { ErknmApproveRemark } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemark';
import { ErknmApproveRemarkChanges } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemarkChanges';
import { ErknmApproveRemarkFieldCodeEnum } from '../../ErknmApproveRemarkModule/enums/ErknmApproveRemarkFieldCodeEnum';
import { IKnmForm } from '../../../KnmPackage/MainModule/interfaces/IKnmForm';
import {
  useIsStartDateEnabled,
  useIsStopDateEnabled,
} from '../../MainModule/fieldsAvailability/erknmInfoDatesAvailability';

interface IErknmInfoDatesProps {
  isWatchOnly: boolean;
  isApproveRequired: boolean;
  typeCode: IKnmTypeSelectOption['code'] | undefined;
  isSupervisor: boolean;
  formRef: IKnmFormRef | undefined;
  isNewKnm: boolean;
  isCreateModal?: boolean;
  isCopyEnabled?: boolean;
}

interface IErknmInfoDatesStoreProps {
  excludeDates: IErknmInfoExcludeDatesResponse | undefined;
  knoSupervisionStartDate: string | null | undefined;
  created: string | null | undefined;
  settings: IAdminSetting[] | undefined;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  isSupervisor: boolean;
}

const ErknmInfoDatesWithoutStore = ({
  isWatchOnly,
  formRef,
  isNewKnm,
  excludeDates,
  knoSupervisionStartDate,
  settings,
  isSupervisor,
  isApproveRequired,
  typeCode,
  created,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  isCreateModal,
  isCopyEnabled,
}: IErknmInfoDatesProps & IErknmInfoDatesStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const startDate = useField('startDate' as keyof IKnmForm, { subscription: { value: true } }).input.value;
  const stopDate = useField('stopDate' as keyof IKnmForm, { subscription: { value: true } }).input.value;
  const kindKnm = useField('kindKnm' as keyof IKnmForm, { subscription: { value: true } }).input.value?.code;
  const kindKnmField = useField('kindKnm' as keyof IKnmForm, { subscription: { value: true } });
  const [isStartTimeDisabled, setIsStartTimeDisabled] = useState(true);
  const [isStopTimeDisabled, setIsStopTimeDisabled] = useState(true);
  const [knmMaxStopDate, setMaxDate] = useState<Date | undefined>(undefined);

  const isTypeCodeVP = typeCode === KnmTypeCodesEnum.VP;

  const isUseKnoSupervisorStartDate = isLaterThanAdminSetting(
    created || undefined,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_36_STARTS_FROM,
  );

  useEffect(() => {
    if (kindKnm === KnmKindCodesEnum.KZ && excludeDates?.dateIn25WorkingDays) {
      setMaxDate(
        parseDate({
          date: excludeDates.dateIn25WorkingDays,
          parseFormat: DATE_FORMAT_LONG_FNS,
        }),
      );
    } else {
      setMaxDate(undefined);
    }
    onClearDateStop();
  }, [kindKnm, excludeDates]);

  useEffect(() => {
    subscribeGetExcludeDates(formRef, dispatch, isNewKnm);
  }, [dispatch, formRef]);

  const isTimeDisabled = useMemo(() => {
    return !(isSupervisor && (isKnmStatusFillInProcess || isKnmStatusReadyForApprove));
  }, [isSupervisor, isKnmStatusFillInProcess, isKnmStatusReadyForApprove]);

  useEffect(() => {
    setIsStartTimeDisabled(isTimeDisabled);
    setIsStopTimeDisabled(isTimeDisabled);
  }, [isTimeDisabled, formRef]);

  const isDisableStartDate = !useIsStartDateEnabled(isNewKnm, isCreateModal);
  const isDisableStopDate = !useIsStopDateEnabled(isNewKnm, isCreateModal);

  // Ограничения на даты начала и окончания КНМ в зависимости от параметров
  const [knmStartMinDate, setKnmStartMinDate] = useState<Date | undefined>();
  const [knmEndMinDate, setKnmEndMinDate] = useState<Date | undefined>();
  const [knmStartExcludeDates, setKnmStartExcludeDates] = useState<Date[] | undefined>();
  const [knmEndExcludeDates, setKnmEndExcludeDates] = useState<Date[] | undefined>();

  const DATE_FORMAT_LONG_REGEX = /^\d{2}\.\d{2}\.\d{4}$/;

  const calculateStartMinDate = useCallback(() => {
    if (isUseKnoSupervisorStartDate && knoSupervisionStartDate) {
      const formattedDate = formatDate({ date: knoSupervisionStartDate, dateFormat: SYSTEM_DATE_FORMAT_FNS });

      if (excludeDates) {
        setKnmStartMinDate(getMaxDate(new Date(excludeDates.startDate.minDate), new Date(formattedDate)));
      } else {
        setKnmStartMinDate(new Date(formattedDate));
      }
    }
  }, [knoSupervisionStartDate, isUseKnoSupervisorStartDate, excludeDates]);

  useEffect(() => {
    if (excludeDates) {
      setKnmStartMinDate(new Date(excludeDates.startDate.minDate));
      const parsedStartDate = parseDateStrict(startDate, DATE_FORMAT_LONG_FNS);

      if (parsedStartDate) {
        setKnmEndMinDate(max([new Date(excludeDates.stopDate.minDate), parsedStartDate]));
      } else {
        setKnmEndMinDate(new Date(excludeDates.stopDate.minDate));
      }

      setKnmStartExcludeDates(excludeDates.startDate.excludeDates.map(date => new Date(date)));
      setKnmEndExcludeDates(excludeDates.stopDate.excludeDates.map(date => new Date(date)));

      if (kindKnm !== KnmKindCodesEnum.KZ) {
        const currentStartDate = formRef?.current.getFieldState('startDate')?.value;

        if (
          currentStartDate &&
          DATE_FORMAT_LONG_REGEX.test(currentStartDate) &&
          !isNewKnm &&
          !isDisableStartDate &&
          !isDateValid(currentStartDate, knmStartMinDate, knmStartExcludeDates)
        ) {
          formRef?.current.change('startDate');
        }

        const currentStopDate = formRef?.current.getFieldState('stopDate')?.value;

        if (!isNewKnm && !isDisableStopDate && !isDateValid(currentStopDate, knmEndMinDate, knmEndExcludeDates)) {
          formRef?.current.change('stopDate');
        }

        calculateStartMinDate();
      }
    }
  }, [excludeDates, isDisableStartDate, isDisableStopDate, isDateValid, formRef, startDate]);

  useEffect(() => {
    calculateStartMinDate();
  }, [knoSupervisionStartDate, isUseKnoSupervisorStartDate]);

  const isShow4131 = isLaterThanAdminSetting(
    created || undefined,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_31_STARTS_FROM,
  );

  const isShowTimePickers = isShow4131 && isTypeCodeVP && isApproveRequired;

  function getFormattedTime(hours?: number) {
    const dateForTime = new Date();

    dateForTime.setHours(hours || 0, 0, 0, 0);

    return dateForTime.toString();
  }

  useEffect(() => {
    if (isShowTimePickers && isCreateModal) {
      formRef?.current.change('startTime', getFormattedTime(9));
      formRef?.current.change('stopTime', getFormattedTime(18));
    }
  }, [isShowTimePickers, isCreateModal]);

  /**
   * Функция очистки поля "Основание"
   */
  const onClearReason = useCallback(() => {
    if (isCreateModal) {
      formRef?.current.change('reasonsErknm');
      formRef?.current.change('approveRequired');
    }
  }, [isCreateModal]);

  const parseDateStrict = (dateStr: string, format: string): Date | undefined => {
    if (!dateStr) return undefined;
    const refDate = new Date(2000, 0, 1);
    const parsed = parse(dateStr, format, refDate);

    return Number.isNaN(parsed.getTime()) ? undefined : startOfDay(parsed);
  };

  /**
   * Функция очистки поля "Дата окончания"
   */
  const onClearDateStop = useCallback(() => {
    const currentStartDate = formRef?.current.getFieldState('startDate')?.value;
    const currentStopDate = formRef?.current.getFieldState('stopDate')?.value;

    if (!currentStopDate || !currentStartDate) {
      return;
    }
    const stopDateParsed = parseDateStrict(currentStopDate, DATE_FORMAT_LONG_FNS);
    const startDateParsed = parseDateStrict(currentStartDate, DATE_FORMAT_LONG_FNS);

    if (!isCorrectStopDate(stopDateParsed, startDateParsed) && isCreateModal) {
      formRef?.current.change('stopDate');
    }
    if (!isCorrectStartDate(startDateParsed) && isCreateModal) {
      formRef?.current.change('startDate');
    }
  }, [isCreateModal, excludeDates, kindKnm]);

  /**
   * Функция очистки поля "Основание", если его значения нет в списке
   */
  const onClearNoMatchingReason = () => {
    const firstReason = formRef?.current?.getState().values.reasonsErknm?.[0];

    if (shouldClearReason(firstReason)) {
      onClearReason();
    }
  };

  const shouldClearReason = (firstReason: IKnmErknmFormReason | undefined) => {
    if (!firstReason) return false;
    const dateBeginAsDate = parseDate({ date: firstReason?.dateBegin || '', parseFormat: DATE_FORMAT_LONG_FNS });
    const dateEndAsDate = parseDate({ date: firstReason?.dateEnd || '', parseFormat: DATE_FORMAT_LONG_FNS });

    if (firstReason?.dateBegin && firstReason.dateEnd && startDate && stopDate) {
      return !(isBefore(dateBeginAsDate || 0, startDate || 0) && isAfter(dateEndAsDate || 0, stopDate || 0));
    }
    if (firstReason?.dateBegin) {
      return !isBefore(dateBeginAsDate || 0, startDate || 0);
    }
  };

  /**
   * Функция изменения даты начала/окончания мероприятия
   */
  const onChangeDate = useCallback(
    (date: string | Date, type: 'startTime' | 'stopTime') => {
      // @ts-ignore
      formRef?.current.change(type, date);

      if (isNewKnm) {
        onClearNoMatchingReason();
      }

      if (isNewKnm && date) {
        if (type === 'startTime') {
          setIsStartTimeDisabled(false);
          formRef?.current.change('startTime', getFormattedTime());
        }
        if (type === 'stopTime') {
          setIsStopTimeDisabled(false);
          formRef?.current.change('stopTime', getFormattedTime());
        }
      } else if (!isNewKnm && date) {
        if (type === 'startTime') {
          setIsStartTimeDisabled(isTimeDisabled);
        }
        if (type === 'stopTime') {
          setIsStopTimeDisabled(isTimeDisabled);
        }
      } else {
        if (type === 'startTime') {
          setIsStartTimeDisabled(isTimeDisabled);
        }
        if (type === 'stopTime') {
          setIsStopTimeDisabled(isTimeDisabled);
        }
      }
    },
    [isKnmStatusFillInProcess, isKnmStatusReadyForApprove, onClearReason, onClearNoMatchingReason, formRef],
  );

  const approveRequiredField = useField('approveRequired' as keyof IKnmForm, { subscription: { value: true } });
  const typeErknmField = useField('typeErknm' as keyof IKnmForm, { subscription: { value: true } });
  const reasonTypeField = useField('reasonType' as keyof IKnmForm, { subscription: { value: true } });

  const updateExcludeDatesWithStartDate = useCallback(
    (newStartDate: string | Date | undefined) => {
      if (kindKnm !== KnmKindCodesEnum.KZ || !startDate || !excludeDates) {
        return undefined;
      }
      const formattedStartDate = newStartDate
        ? formatDate({
            date: newStartDate,
            dateFormat: SYSTEM_DATE_FORMAT_FNS,
          })
        : undefined;
      const firstReason = formRef?.current?.getState().values.reasonsErknm?.[0];

      dispatch(
        knmInfoGetExcludeDates({
          approveRequiredCode: approveRequiredField.input.value?.id ?? '',
          kindCode: kindKnmField.input.value?.code ?? '',
          typeCode: typeErknmField.input.value?.code ?? '',
          reasonTypeId: firstReason?.type?.id,
          reasonName: firstReason?.type?.name,
          selectedStartDate: formattedStartDate,
          isNewKnm,
        }),
      );
      onClearDateStop();
    },
    [dispatch, approveRequiredField, kindKnmField, typeErknmField, reasonTypeField],
  );

  const validateStopDate = useCallback(
    (value: string | undefined): string | undefined => {
      if (kindKnm !== KnmKindCodesEnum.KZ || !value || !startDate || !excludeDates) {
        return undefined;
      }

      const stopDateParsed = parseDate({ date: value, parseFormat: DATE_FORMAT_LONG_FNS });
      const startDateParsed = parseDate({ date: startDate, parseFormat: DATE_FORMAT_LONG_FNS });

      if (!isCorrectStopDate(stopDateParsed, startDateParsed)) {
        return 'Дата окончания не может быть позже даты начала более чем на 25 дней';
      }

      return undefined;
    },
    [kindKnm, startDate, excludeDates],
  );

  const isCorrectStopDate = (stopDateParsed: Date | undefined, startDateParsed: Date | undefined): boolean => {
    if (!excludeDates) {
      return true;
    }
    const dateIn25WorkingDays = parseDateStrict(excludeDates.dateIn25WorkingDays, SYSTEM_DATE_FORMAT_FNS);

    if (!stopDateParsed || !startDateParsed || !dateIn25WorkingDays) {
      return true;
    }

    if (dateIn25WorkingDays < stopDateParsed) {
      return false;
    }

    return true;
  };

  const isCorrectStartDate = (startDateParsed: Date | undefined): boolean => {
    if (!excludeDates) {
      return true;
    }
    const firstAvailableWorkingDays = parseDateStrict(excludeDates.startDate.minDate, SYSTEM_DATE_FORMAT_FNS);

    if (!startDateParsed || !firstAvailableWorkingDays) {
      return true;
    }

    if (startDateParsed < firstAvailableWorkingDays) {
      return false;
    }

    return true;
  };

  const startDatePickerField = (date: string | Date) => {
    updateExcludeDatesWithStartDate(date);
    if (isShowTimePickers) {
      onChangeDate(date, 'startTime');
    } else {
      onClearNoMatchingReason();
    }
  };

  return (
    <FieldBlock
      label={
        isNewKnm
          ? undefined
          : getTitleFieldWithRedStar({
              label: `Даты проведения КНМ`,
              sectionName: `dates`,
              isPm: false,
              isDecision: false,
              isKnm: true,
            })
      }
      className={styles.FullWidth}
      button={<ErknmApproveRemarkAddButton sectionCode={ErknmApproveRemarkSectionCodeEnum.DATES} />}
    >
      <ErknmApproveRemark sectionCode={ErknmApproveRemarkSectionCodeEnum.DATES} />
      <Row>
        <FieldBlock
          className={isNewKnm ? styles.FieldWithOutTooltip : undefined}
          color={isNewKnm ? undefined : 'black'}
          id={START_DATE_BLOCK_ID}
          label={
            <div className={isNewKnm ? '' : 'shared-flex'}>
              {getTitleFieldWithRedStar({
                label: `Дата начала мероприятия`,
                field: `startDate`,
                isPm: false,
                isDecision: false,
                isKnm: true,
              })}
              <ErknmApproveRemarkChanges
                sectionCode={ErknmApproveRemarkSectionCodeEnum.DATES}
                fieldCode={ErknmApproveRemarkFieldCodeEnum.START_DATE}
                oldValue={startDate}
              />
            </div>
          }
          margin="top"
          size={isShowTimePickers ? 'col-4' : 'half'}
        >
          <DatePickerField
            id="startDate"
            name="startDate"
            placeholder="Выберите дату"
            disabled={!isCopyEnabled ? isDisableStartDate : false}
            minDate={knmStartMinDate}
            excludeDates={knmStartExcludeDates}
            onChange={date => startDatePickerField(date)}
            opensOnTop={true}
          />
        </FieldBlock>
        {isShowTimePickers && (
          <FieldBlock
            className={isNewKnm ? styles.FieldWithOutTooltip : undefined}
            color={isNewKnm ? undefined : 'black'}
            id={START_TIME_BLOCK_ID}
            label={getTitleFieldWithRedStar({
              label: `Время начала мероприятия`,
              field: `startTime`,
              isPm: false,
              isDecision: false,
              isKnm: true,
            })}
            margin="top"
            size={isShowTimePickers ? 'col-4' : 'half'}
          >
            <TimePickerField
              id="startTime"
              name="startTime"
              placeholder="Выберите время"
              disabled={isCreateModal ? !isShowTimePickers : isWatchOnly || isStartTimeDisabled}
              minDate={knmStartMinDate}
              excludeDates={knmStartExcludeDates}
              opensOnTop={true}
            />
          </FieldBlock>
        )}
        <FieldBlock
          className={isNewKnm ? styles.FieldWithOutTooltip : undefined}
          color={isNewKnm ? undefined : 'black'}
          id={STOP_DATE_BLOCK_ID}
          label={
            <div className={isNewKnm ? '' : 'shared-flex'}>
              {getTitleFieldWithRedStar({
                label: `Дата окончания мероприятия`,
                field: `stopDate`,
                isPm: false,
                isDecision: false,
                isKnm: true,
              })}
              <ErknmApproveRemarkChanges
                sectionCode={ErknmApproveRemarkSectionCodeEnum.DATES}
                fieldCode={ErknmApproveRemarkFieldCodeEnum.STOP_DATE}
                oldValue={stopDate}
              />
            </div>
          }
          margin="top"
          size={isShowTimePickers ? 'col-4' : 'half'}
        >
          <DatePickerField
            id="stopDate"
            name="stopDate"
            placeholder="Выберите дату"
            disabled={isDisableStopDate}
            onChange={date => (isShowTimePickers ? onChangeDate(date, 'stopTime') : onClearNoMatchingReason())}
            opensOnTop={true}
            excludeDates={startDate ? undefined : knmEndExcludeDates}
            minDate={knmEndMinDate}
            maxDate={knmMaxStopDate}
            validate={validateStopDate}
          />
        </FieldBlock>
        {isShowTimePickers && (
          <FieldBlock
            className={isNewKnm ? styles.FieldWithOutTooltip : undefined}
            color={isNewKnm ? undefined : 'black'}
            id={STOP_TIME_BLOCK_ID}
            label={getTitleFieldWithRedStar({
              label: `Время окончания мероприятия`,
              field: `stopTime`,
              isPm: false,
              isDecision: false,
              isKnm: true,
            })}
            margin="top"
            size={isShowTimePickers ? 'col-4' : 'half'}
          >
            <TimePickerField
              id="stopTime"
              name="stopTime"
              placeholder="Выберите время"
              disabled={isCreateModal ? !isShowTimePickers : isWatchOnly || isStopTimeDisabled}
              minDate={knmEndMinDate}
              excludeDates={knmEndExcludeDates}
              opensOnTop={true}
            />
          </FieldBlock>
        )}
      </Row>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmInfoDatesStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
  excludeDates: getKnmExcludeDates(state),
  knoSupervisionStartDate: getKnoSupervisionStartDate(state),
  created: getKnmDataCreated(state),
  settings: getAdminFetchSettings(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
});

export const ErknmInfoDates = connect(mapStateToProps)(ErknmInfoDatesWithoutStore);
